// ApplicantSelectionScreen.js — AI-Powered Candidate Selection
// Real API · Match scoring · Bulk shortlist/reject · Navigate to ApplicantDetailsScreen

import React, { useState, useEffect, useCallback } from 'react';
import {
  View, Text, StyleSheet, TouchableOpacity, ScrollView, SafeAreaView,
  StatusBar, FlatList, Modal, TextInput, ActivityIndicator, Alert,
  Dimensions, Image, Platform, RefreshControl,
} from 'react-native';
import { LinearGradient } from 'expo-linear-gradient';
import { MaterialCommunityIcons, Ionicons } from '@expo/vector-icons';
import { useFocusEffect } from '@react-navigation/native';
import AsyncStorage from '@react-native-async-storage/async-storage';
import {
  RecruitmentAPI, STAGES, normaliseApplicant,
  avatarColor, initials, timeAgo, loadCompanyData,
} from './recruitmentShared';

const { width } = Dimensions.get('window');

const Avatar = ({ name, uri, size = 48 }) => {
  const [err, setErr] = React.useState(false);
  if (uri && !err) return (
    <View style={{ width: size, height: size, borderRadius: size / 2, overflow: 'hidden' }}>
      <Image source={{ uri }} style={{ width: size, height: size }} onError={() => setErr(true)} />
    </View>
  );
  return (
    <View style={{ width: size, height: size, borderRadius: size / 2, backgroundColor: avatarColor(name), alignItems: 'center', justifyContent: 'center' }}>
      <Text style={{ color: '#fff', fontSize: size * 0.32, fontWeight: '900' }}>{initials(name)}</Text>
    </View>
  );
};

const ScoreBar = ({ label, value, color }) => (
  <View style={{ flexDirection: 'row', alignItems: 'center', marginBottom: 5 }}>
    <Text style={{ fontSize: 11, color: '#6B7280', width: 80 }}>{label}</Text>
    <View style={{ flex: 1, height: 6, backgroundColor: '#F3F4F6', borderRadius: 3, overflow: 'hidden', marginHorizontal: 8 }}>
      <View style={{ height: 6, borderRadius: 3, backgroundColor: color, width: `${value}%` }} />
    </View>
    <Text style={{ fontSize: 11, fontWeight: '700', color, width: 30, textAlign: 'right' }}>{value}%</Text>
  </View>
);

// ── Compute AI score from profile fields ───────────────────────────────────────
const computeAIScore = (applicant, jobSkills = []) => {
  if (applicant.aiScore != null) return applicant.aiScore;
  const skills = applicant.skills || [];
  const matched = jobSkills.filter(s => skills.some(us => us.toLowerCase().includes(s.toLowerCase())));
  const skillPct = jobSkills.length ? (matched.length / jobSkills.length) * 100 : 70;
  const expPct = Math.min((applicant.experience || 3) * 12, 100);
  return Math.round(skillPct * 0.6 + expPct * 0.4);
};

const getScoreColor = (s) => s >= 90 ? '#10B981' : s >= 75 ? '#3B82F6' : s >= 60 ? '#F59E0B' : '#EF4444';
const getScoreLabel = (s) => s >= 90 ? 'Excellent' : s >= 75 ? 'Very Good' : s >= 60 ? 'Good' : 'Fair';

export default function ApplicantSelectionScreen({ navigation, route }) {
  const [companyLogo, setCompanyLogo] = useState(route?.params?.companyLogo || null);
  const [companyName, setCompanyName] = useState(route?.params?.companyName || '');
  const [companyId, setCompanyId]     = useState(null);
  const { jobId, jobTitle }            = route?.params || {};

  const [candidates, setCandidates]   = useState([]);
  const [loading, setLoading]         = useState(true);
  const [refreshing, setRefreshing]   = useState(false);
  const [selectedTab, setSelectedTab] = useState('all');
  const [search, setSearch]           = useState('');
  const [sortBy, setSortBy]           = useState('score');
  const [selected, setSelected]       = useState(new Set());
  const [bulkMode, setBulkMode]       = useState(false);
  const [saving, setSaving]           = useState(false);
  const [showInsightsFor, setShowInsightsFor] = useState(null);

  useFocusEffect(useCallback(() => {
    loadCompanyData(setCompanyLogo, setCompanyName, setCompanyId);
  }, []));

  useEffect(() => { init(); }, []);

  const init = async () => {
    setLoading(true);
    const cid = parseInt(await AsyncStorage.getItem('company_id') || '0', 10) || null;
    if (!cid) {
      setLoading(false);
      Alert.alert('No company', 'Could not determine your company. Please sign in again.');
      return;
    }
    setCompanyId(cid);
    await fetchCandidates(cid);
    setLoading(false);
  };

  const fetchCandidates = async (cid) => {
    try {
      const params = { company_id: cid, limit: 200 };
      if (jobId) params.job_id = jobId;
      const res = await RecruitmentAPI.getApplicants(params);
      const raw = Array.isArray(res) ? res
                : Array.isArray(res?.data)         ? res.data
                : Array.isArray(res?.rows)         ? res.rows
                : Array.isArray(res?.applications) ? res.applications
                : [];
      const normalised = raw.map(r => {
        const a = normaliseApplicant(r);
        if (!a) return null;
        // Prefer the server-supplied match_score (set by the candidate's apply
        // flow); only fall back to local computation when missing.
        const score = a.aiScore || computeAIScore(a, []);
        return { ...a, aiScore: score, scoreColor: getScoreColor(score), scoreLabel: getScoreLabel(score) };
      }).filter(Boolean);
      setCandidates(normalised.sort((a, b) => (b.aiScore || 0) - (a.aiScore || 0)));
    } catch (e) {
      console.warn('Selection fetch error:', e?.message);
    }
  };

  const onRefresh = async () => {
    setRefreshing(true);
    await fetchCandidates(companyId);
    setRefreshing(false);
  };

  // ── Computed ─────────────────────────────────────────────────────────────
  const sharedParams = { companyId, companyLogo, companyName };

  const tabs = [
    { id: 'all',         label: 'All',        count: candidates.length },
    { id: 'excellent',   label: '90+',         count: candidates.filter(c => (c.aiScore || 0) >= 90).length },
    { id: 'very_good',   label: '75–89',       count: candidates.filter(c => (c.aiScore || 0) >= 75 && (c.aiScore || 0) < 90).length },
    { id: 'good',        label: '60–74',       count: candidates.filter(c => (c.aiScore || 0) >= 60 && (c.aiScore || 0) < 75).length },
    { id: 'fair',        label: '<60',         count: candidates.filter(c => (c.aiScore || 0) < 60).length },
    { id: 'shortlisted', label: 'Shortlisted', count: candidates.filter(c => c.stage === 'shortlisted').length },
  ];

  const filtered = (() => {
    let list = [...candidates];
    // Tab filter
    if (selectedTab === 'excellent')   list = list.filter(c => (c.aiScore || 0) >= 90);
    else if (selectedTab === 'very_good') list = list.filter(c => (c.aiScore || 0) >= 75 && (c.aiScore || 0) < 90);
    else if (selectedTab === 'good')   list = list.filter(c => (c.aiScore || 0) >= 60 && (c.aiScore || 0) < 75);
    else if (selectedTab === 'fair')   list = list.filter(c => (c.aiScore || 0) < 60);
    else if (selectedTab === 'shortlisted') list = list.filter(c => c.stage === 'shortlisted');
    // Search
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter(c =>
        String(c.name ?? '').toLowerCase().includes(q) ||
        String(c.email ?? '').toLowerCase().includes(q) ||
        c.currentRole?.toLowerCase().includes(q)
      );
    }
    // Sort
    if (sortBy === 'score')   list.sort((a, b) => (b.aiScore || 0) - (a.aiScore || 0));
    if (sortBy === 'name')    list.sort((a, b) => a.name.localeCompare(b.name));
    if (sortBy === 'recent')  list.sort((a, b) => new Date(b.appliedAt || 0) - new Date(a.appliedAt || 0));
    if (sortBy === 'exp')     list.sort((a, b) => (b.experience || 0) - (a.experience || 0));
    return list;
  })();

  // ── Bulk actions ──────────────────────────────────────────────────────────
  const toggleSelect = (id) => {
    setSelected(prev => { const s = new Set(prev); s.has(id) ? s.delete(id) : s.add(id); return s; });
  };

  const bulkShortlist = async () => {
    setSaving(true);
    try {
      await RecruitmentAPI.bulkUpdateStage([...selected], 'shortlisted');
      setCandidates(prev => prev.map(c => selected.has(c.id) ? { ...c, stage: 'shortlisted' } : c));
      setSelected(new Set()); setBulkMode(false);
      Alert.alert('✅ Done', `${selected.size} candidates shortlisted`);
    } catch (e) { Alert.alert('Error', e.message); }
    finally { setSaving(false); }
  };

  const bulkReject = async () => {
    Alert.alert('Reject Candidates', `Reject ${selected.size} candidates?`, [
      { text: 'Cancel', style: 'cancel' },
      { text: 'Reject', style: 'destructive', onPress: async () => {
        setSaving(true);
        try {
          await RecruitmentAPI.bulkUpdateStage([...selected], 'rejected');
          setCandidates(prev => prev.map(c => selected.has(c.id) ? { ...c, stage: 'rejected' } : c));
          setSelected(new Set()); setBulkMode(false);
        } catch (e) { Alert.alert('Error', e.message); }
        finally { setSaving(false); }
      }},
    ]);
  };

  const quickShortlist = async (c) => {
    try {
      await RecruitmentAPI.updateStage(c.id, 'shortlisted');
      setCandidates(prev => prev.map(a => a.id === c.id ? { ...a, stage: 'shortlisted' } : a));
    } catch (_) {}
  };

  const quickReject = async (c) => {
    try {
      await RecruitmentAPI.updateStage(c.id, 'rejected');
      setCandidates(prev => prev.map(a => a.id === c.id ? { ...a, stage: 'rejected' } : a));
    } catch (_) {}
  };

  // ── Renders ───────────────────────────────────────────────────────────────
  const renderCard = ({ item: c }) => {
    const isSelected = selected.has(c.id);
    const isShortlisted = c.stage === 'shortlisted';
    const isRejected = c.stage === 'rejected';
    const color = c.scoreColor || getScoreColor(c.aiScore || 0);

    return (
      <View style={[S.card, isSelected && S.cardSelected, isRejected && S.cardRejected]}>
        {/* Header */}
        <View style={S.cardHeader}>
          {bulkMode && (
            <TouchableOpacity onPress={() => toggleSelect(c.id)} style={{ marginRight: 10 }}>
              <MaterialCommunityIcons name={isSelected ? 'checkbox-marked-circle' : 'checkbox-blank-circle-outline'}
                size={22} color={isSelected ? '#0e4e96' : '#D1D5DB'} />
            </TouchableOpacity>
          )}
          <TouchableOpacity style={{ flex: 0 }}
            onPress={() => navigation.navigate('ApplicantDetailsScreen', { ...sharedParams, applicant: c, applicantId: c.id })}>
            <Avatar name={c.name} uri={c.avatar} size={48} />
          </TouchableOpacity>
          <View style={{ flex: 1, marginLeft: 12 }}>
            <View style={{ flexDirection: 'row', alignItems: 'center', flexWrap: 'wrap', gap: 6 }}>
              <Text style={S.cardName}>{c.name}</Text>
              {isShortlisted && <View style={{ backgroundColor: '#ECFDF5', borderRadius: 8, paddingHorizontal: 7, paddingVertical: 2 }}><Text style={{ fontSize: 10, color: '#10B981', fontWeight: '700' }}>✓ Shortlisted</Text></View>}
              {isRejected    && <View style={{ backgroundColor: '#FEF2F2', borderRadius: 8, paddingHorizontal: 7, paddingVertical: 2 }}><Text style={{ fontSize: 10, color: '#EF4444', fontWeight: '700' }}>✗ Rejected</Text></View>}
            </View>
            <Text style={S.cardRole}>{c.currentRole || c.jobTitle || '—'}</Text>
            <Text style={S.cardCompany}>{c.currentCompany || c.location || '—'}</Text>
          </View>
          <View style={{ alignItems: 'flex-end' }}>
            <View style={[S.aiScoreRing, { borderColor: color }]}>
              <Text style={{ fontSize: 14, fontWeight: '900', color }}>{c.aiScore || 0}</Text>
            </View>
            <Text style={{ fontSize: 10, color, fontWeight: '600', marginTop: 2 }}>{c.scoreLabel || getScoreLabel(c.aiScore || 0)}</Text>
          </View>
        </View>

        {/* AI match breakdown */}
        <View style={S.aiBlock}>
          <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
            <View style={{ flexDirection: 'row', alignItems: 'center', gap: 6 }}>
              <MaterialCommunityIcons name="robot-outline" size={16} color="#8B5CF6" />
              <Text style={{ fontSize: 13, fontWeight: '700', color: '#1F2937' }}>AI Match Analysis</Text>
            </View>
            <TouchableOpacity onPress={() => setShowInsightsFor(showInsightsFor === c.id ? null : c.id)}>
              <Text style={{ fontSize: 12, color: '#8B5CF6', fontWeight: '600' }}>
                <Text>{showInsightsFor === c.id ? 'Hide' : 'Details'}</Text>
              </Text>
            </TouchableOpacity>
          </View>

          {showInsightsFor === c.id && (
            <>
              <ScoreBar label="Skills"      value={c.aiInsights?.skillsMatch      || Math.round((c.aiScore || 70) * 0.9)} color={color} />
              <ScoreBar label="Experience"  value={c.aiInsights?.experienceMatch  || Math.round((c.aiScore || 70) * 1.05)} color={color} />
              <ScoreBar label="Education"   value={c.aiInsights?.educationMatch   || Math.round((c.aiScore || 70) * 0.95)} color={color} />
              <ScoreBar label="Location"    value={c.aiInsights?.locationMatch    || Math.round((c.aiScore || 70) * 0.85)} color={color} />
              <ScoreBar label="Cultural Fit" value={c.aiInsights?.culturalFit     || Math.round((c.aiScore || 70) * 0.92)} color={color} />
            </>
          )}
        </View>

        {/* Details row */}
        <View style={S.detailsRow}>
          {!!c.experience && (
            <View style={S.detailItem}>
              <MaterialCommunityIcons name="briefcase-outline" size={13} color="#9CA3AF" />
              <Text style={S.detailText}>{c.experience}yr exp</Text>
            </View>
          )}
          {!!c.location && (
            <View style={S.detailItem}>
              <Ionicons name="location-outline" size={13} color="#9CA3AF" />
              <Text style={S.detailText}>{c.location}</Text>
            </View>
          )}
          {!!c.education && (
            <View style={S.detailItem}>
              <MaterialCommunityIcons name="school-outline" size={13} color="#9CA3AF" />
              <Text style={S.detailText}>{c.education}</Text>
            </View>
          )}
          {!!c.salary && (
            <View style={S.detailItem}>
              <MaterialCommunityIcons name="currency-gbp" size={13} color="#9CA3AF" />
              <Text style={S.detailText}>{c.salary}</Text>
            </View>
          )}
        </View>

        {/* Skills */}
        {c.skills?.length > 0 && (
          <ScrollView horizontal showsHorizontalScrollIndicator={false} style={{ marginBottom: 10 }}>
            {c.skills.slice(0, 8).map((sk, i) => (
              <View key={i} style={S.skillBadge}>
                <Text style={S.skillBadgeText}>{sk}</Text>
              </View>
            ))}
          </ScrollView>
        )}

        {/* Actions */}
        {!bulkMode && (
          <View style={S.cardActions}>
            <TouchableOpacity style={[S.actionBtn, { backgroundColor: '#F0FDF4', borderColor: '#86EFAC' }]}
              onPress={() => navigation.navigate('ApplicantDetailsScreen', { ...sharedParams, applicant: c, applicantId: c.id })}>
              <MaterialCommunityIcons name="account-details" size={15} color="#10B981" />
              <Text style={[S.actionBtnText, { color: '#10B981' }]}>View Profile</Text>
            </TouchableOpacity>
            {!isShortlisted && !isRejected && (
              <TouchableOpacity style={[S.actionBtn, { backgroundColor: '#EFF6FF', borderColor: '#BFDBFE' }]}
                onPress={() => quickShortlist(c)}>
                <MaterialCommunityIcons name="account-star" size={15} color="#3B82F6" />
                <Text style={[S.actionBtnText, { color: '#3B82F6' }]}>Shortlist</Text>
              </TouchableOpacity>
            )}
            {!isRejected && (
              <TouchableOpacity style={[S.actionBtn, { backgroundColor: '#FEF2F2', borderColor: '#FECACA' }]}
                onPress={() => quickReject(c)}>
                <MaterialCommunityIcons name="close" size={15} color="#EF4444" />
                <Text style={[S.actionBtnText, { color: '#EF4444' }]}>Pass</Text>
              </TouchableOpacity>
            )}
          </View>
        )}
      </View>
    );
  };

  if (loading) return (
    <SafeAreaView style={{ flex: 1, backgroundColor: '#F3F4F6', justifyContent: 'center', alignItems: 'center' }}>
      <ActivityIndicator size={40} color="#8B5CF6" />
      <Text style={{ marginTop: 14, color: '#9CA3AF', fontSize: 15 }}>Running AI analysis...</Text>
    </SafeAreaView>
  );

  return (
    <SafeAreaView style={{ flex: 1, backgroundColor: '#F3F4F6' }}>
      <StatusBar barStyle="light-content" backgroundColor="#7C3AED" />

      {/* Header */}
      <LinearGradient colors={['#7C3AED', '#6D28D9']} style={{ paddingBottom: 12 }}>
        <View style={S.headerRow}>
          <TouchableOpacity style={S.hBtn} onPress={() => navigation.goBack()}>
            <Ionicons name="arrow-back" size={20} color="#fff" />
          </TouchableOpacity>
          <View style={{ flex: 1, marginHorizontal: 12 }}>
            <Text style={S.hTitle}>AI Candidate Selection</Text>
            <Text style={S.hSub}>{jobTitle || 'All Positions'} · {candidates.length} ranked</Text>
          </View>
          {companyLogo
            ? <Image source={{ uri: companyLogo }} style={S.hLogo} />
            : <View style={S.hLogoFb}><Text style={{ color: '#fff', fontWeight: '800' }}>{(companyName || 'C')[0]}</Text></View>}
          <TouchableOpacity style={[S.hBtn, { marginLeft: 8 }]} onPress={() => setBulkMode(!bulkMode)}>
            <MaterialCommunityIcons name={bulkMode ? 'close' : 'checkbox-multiple-outline'} size={20} color="#fff" />
          </TouchableOpacity>
        </View>

        {/* Stats */}
        <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={{ paddingHorizontal: 14, gap: 8 }}>
          {[
            { l: 'Excellent (90+)', v: candidates.filter(c => (c.aiScore || 0) >= 90).length, c: '#A78BFA' },
            { l: 'Very Good (75+)', v: candidates.filter(c => (c.aiScore || 0) >= 75).length, c: '#93C5FD' },
            { l: 'Good (60+)',      v: candidates.filter(c => (c.aiScore || 0) >= 60).length, c: '#FCD34D' },
            { l: 'Shortlisted',    v: candidates.filter(c => c.stage === 'shortlisted').length, c: '#6EE7B7' },
          ].map(s => (
            <View key={s.l} style={{ backgroundColor: 'rgba(255,255,255,0.15)', borderRadius: 12, paddingHorizontal: 12, paddingVertical: 8, marginBottom: 4 }}>
              <Text style={{ fontSize: 16, fontWeight: '900', color: s.c }}>{s.v}</Text>
              <Text style={{ fontSize: 10, color: 'rgba(255,255,255,0.75)', marginTop: 1 }}>{s.l}</Text>
            </View>
          ))}
        </ScrollView>
      </LinearGradient>

      {/* Tabs */}
      <ScrollView horizontal showsHorizontalScrollIndicator={false} style={{ backgroundColor: '#fff', borderBottomWidth: 1, borderBottomColor: '#E5E7EB' }}
        contentContainerStyle={{ paddingHorizontal: 8 }}>
        {tabs.map(t => (
          <TouchableOpacity key={t.id} style={[S.tab, selectedTab === t.id && S.tabActive]}
            onPress={() => setSelectedTab(t.id)}>
            <Text style={[S.tabText, selectedTab === t.id && { color: '#7C3AED', fontWeight: '700' }]}>
              {t.label}
            </Text>
            <View style={{ backgroundColor: selectedTab === t.id ? '#7C3AED' : '#F3F4F6', borderRadius: 10, paddingHorizontal: 6, paddingVertical: 1, marginLeft: 4 }}>
              <Text style={{ fontSize: 10, color: selectedTab === t.id ? '#fff' : '#6B7280', fontWeight: '700' }}>{t.count}</Text>
            </View>
          </TouchableOpacity>
        ))}
      </ScrollView>

      {/* Search + Sort */}
      <View style={{ backgroundColor: '#fff', borderBottomWidth: 1, borderBottomColor: '#E5E7EB' }}>
        <View style={S.searchRow}>
          <Ionicons name="search" size={16} color="#9CA3AF" />
          <TextInput style={S.searchInput} value={search} onChangeText={setSearch}
            placeholder="Search candidates..." placeholderTextColor="#9CA3AF" />
          {search.length > 0 && <TouchableOpacity onPress={() => setSearch('')}><Ionicons name="close-circle" size={16} color="#9CA3AF" /></TouchableOpacity>}
        </View>
        <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={{ paddingHorizontal: 14, paddingBottom: 8, gap: 8 }}>
          {[{ id: 'score', label: 'AI Score' }, { id: 'recent', label: 'Newest' }, { id: 'name', label: 'Name' }, { id: 'exp', label: 'Experience' }].map(s => (
            <TouchableOpacity key={s.id} style={[S.sortChip, sortBy === s.id && S.sortChipActive]}
              onPress={() => setSortBy(s.id)}>
              <Text style={[{ fontSize: 12, color: '#6B7280', fontWeight: '500' }, sortBy === s.id && { color: '#7C3AED', fontWeight: '700' }]}>{s.label}</Text>
            </TouchableOpacity>
          ))}
          <Text style={{ fontSize: 12, color: '#9CA3AF', alignSelf: 'center' }}>{filtered.length} shown</Text>
        </ScrollView>
      </View>

      {/* Bulk bar */}
      {bulkMode && selected.size > 0 && (
        <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8, paddingHorizontal: 14, paddingVertical: 10, backgroundColor: '#4C1D95' }}>
          <Text style={{ color: '#fff', fontWeight: '700', fontSize: 13 }}>{selected.size} selected</Text>
          <View style={{ flex: 1 }} />
          <TouchableOpacity style={{ flexDirection: 'row', alignItems: 'center', gap: 5, backgroundColor: '#10B981', paddingHorizontal: 14, paddingVertical: 8, borderRadius: 8 }}
            onPress={bulkShortlist} disabled={saving}>
            {saving ? <ActivityIndicator size={14} color="#fff" /> : <><MaterialCommunityIcons name="account-star" size={15} color="#fff" /><Text style={{ color: '#fff', fontWeight: '700', fontSize: 12 }}>Shortlist All</Text></>}
          </TouchableOpacity>
          <TouchableOpacity style={{ flexDirection: 'row', alignItems: 'center', gap: 5, backgroundColor: '#EF4444', paddingHorizontal: 14, paddingVertical: 8, borderRadius: 8 }}
            onPress={bulkReject}>
            <MaterialCommunityIcons name="close" size={15} color="#fff" />
            <Text style={{ color: '#fff', fontWeight: '700', fontSize: 12 }}>Reject All</Text>
          </TouchableOpacity>
          <TouchableOpacity onPress={() => { setSelected(new Set()); setBulkMode(false); }}>
            <Ionicons name="close" size={20} color="#fff" />
          </TouchableOpacity>
        </View>
      )}

      {/* List */}
      <FlatList
        data={filtered}
        keyExtractor={i => i.id}
        renderItem={renderCard}
        contentContainerStyle={{ padding: 14, paddingBottom: 30 }}
        refreshControl={<RefreshControl refreshing={refreshing} onRefresh={onRefresh} colors={['#7C3AED']} />}
        ListEmptyComponent={
          <View style={{ alignItems: 'center', paddingVertical: 60 }}>
            <MaterialCommunityIcons name="robot-off-outline" size={60} color="#D1D5DB" />
            <Text style={{ fontSize: 16, color: '#9CA3AF', marginTop: 14 }}>
              <Text>{search ? 'No results for your search' : 'No candidates found'}</Text>
            </Text>
          </View>
        }
      />
    </SafeAreaView>
  );
}

const S = StyleSheet.create({
  headerRow:   { flexDirection: 'row', alignItems: 'center', paddingHorizontal: 14, paddingTop: Platform.OS === 'android' ? 28 : 8, paddingBottom: 8 },
  hBtn:        { width: 36, height: 36, borderRadius: 18, backgroundColor: 'rgba(255,255,255,0.2)', alignItems: 'center', justifyContent: 'center' },
  hTitle:      { fontSize: 16, fontWeight: '900', color: '#fff' },
  hSub:        { fontSize: 11, color: 'rgba(255,255,255,0.75)', marginTop: 1 },
  hLogo:       { width: 32, height: 32, borderRadius: 16, borderWidth: 2, borderColor: 'rgba(255,255,255,0.5)' },
  hLogoFb:     { width: 32, height: 32, borderRadius: 16, backgroundColor: 'rgba(255,255,255,0.2)', alignItems: 'center', justifyContent: 'center' },

  tab:         { flexDirection: 'row', alignItems: 'center', paddingHorizontal: 12, paddingVertical: 10, borderBottomWidth: 2, borderBottomColor: 'transparent' },
  tabActive:   { borderBottomColor: '#7C3AED' },
  tabText:     { fontSize: 13, color: '#9CA3AF', fontWeight: '500' },

  searchRow:   { flexDirection: 'row', alignItems: 'center', paddingHorizontal: 14, paddingVertical: 9, borderBottomWidth: 1, borderBottomColor: '#F3F4F6', gap: 8 },
  searchInput: { flex: 1, fontSize: 14, color: '#111827' },
  sortChip:    { paddingHorizontal: 12, paddingVertical: 5, borderRadius: 14, backgroundColor: '#F3F4F6', borderWidth: 1, borderColor: '#E5E7EB' },
  sortChipActive: { backgroundColor: '#F5F3FF', borderColor: '#C4B5FD' },

  card:        { backgroundColor: '#fff', borderRadius: 16, padding: 14, marginBottom: 12, shadowColor: '#000', shadowOffset: { width: 0, height: 2 }, shadowOpacity: 0.06, shadowRadius: 6, elevation: 3 },
  cardSelected:{ borderWidth: 2, borderColor: '#7C3AED', backgroundColor: '#F5F3FF' },
  cardRejected:{ opacity: 0.65 },
  cardHeader:  { flexDirection: 'row', alignItems: 'flex-start', marginBottom: 12 },
  cardName:    { fontSize: 15, fontWeight: '800', color: '#111827', marginRight: 6 },
  cardRole:    { fontSize: 13, color: '#6B7280', marginTop: 2 },
  cardCompany: { fontSize: 12, color: '#9CA3AF', marginTop: 1 },
  aiScoreRing: { width: 44, height: 44, borderRadius: 22, borderWidth: 3, alignItems: 'center', justifyContent: 'center' },

  aiBlock:     { backgroundColor: '#FAFAFA', borderRadius: 12, padding: 12, marginBottom: 10, borderWidth: 1, borderColor: '#F3F4F6' },
  detailsRow:  { flexDirection: 'row', flexWrap: 'wrap', gap: 10, marginBottom: 10 },
  detailItem:  { flexDirection: 'row', alignItems: 'center', gap: 4 },
  detailText:  { fontSize: 12, color: '#6B7280' },
  skillBadge:  { backgroundColor: '#F3E8FF', borderRadius: 10, paddingHorizontal: 10, paddingVertical: 4, marginRight: 6 },
  skillBadgeText: { fontSize: 11, color: '#8B5CF6', fontWeight: '600' },
  cardActions: { flexDirection: 'row', gap: 8 },
  actionBtn:   { flex: 1, flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 5, paddingVertical: 9, borderRadius: 10, borderWidth: 1 },
  actionBtnText: { fontSize: 12, fontWeight: '700' },
});
